import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldSelect } from '@/shared/components/custom/form-field';
import { ConvertToTaskField } from './ConvertToTaskField';

export interface ConvertToTaskSelectOption {
  value: string;
  label: string;
  /** Rendered before the label, in the list and in the closed trigger. */
  icon?: React.ReactNode;
}

interface ConvertToTaskSelectProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  options: ConvertToTaskSelectOption[];
  placeholder: string;
  required?: boolean;
  aiLabel?: string;
  hint?: string;
  disabled?: boolean;
  /** Leading icon inside the trigger — the field's own glyph, not the value's. */
  icon?: React.ReactNode;
  /** Blue outline the design gives every AI-filled control. */
  highlighted?: boolean;
  /** Stores the selection as a number, for integer-coded fields like priority. */
  numeric?: boolean;
  'data-testid'?: string;
}

/**
 * A labelled select row of the convert-to-task dialog.
 *
 * Wraps the shared FormFieldSelect so the row keeps the dialog's AI badge and
 * hint layout while the shared component owns validation and Controller wiring.
 */
export function ConvertToTaskSelect<T extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholder,
  required,
  aiLabel,
  hint,
  disabled,
  icon,
  highlighted,
  numeric,
  'data-testid': testId,
}: ConvertToTaskSelectProps<T>) {
  return (
    <ConvertToTaskField
      label={label}
      required={required}
      aiLabel={aiLabel}
      hint={hint}
      htmlFor={name}
    >
      <FormFieldSelect
        control={control}
        name={name}
        label={label}
        options={options.map((option) => ({
          value: option.value,
          label: option.label,
          icon: option.icon,
        }))}
        placeholder={placeholder}
        disabled={disabled}
        startIcon={icon}
        highlighted={highlighted}
        numeric={numeric}
        hideLabel
        data-testid={testId}
      />
    </ConvertToTaskField>
  );
}
